import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from './ui';

const PRIORITY_COLOR = {
  LOW:      { bar: '#94A3B8', bg: '#F8FAFC', pill: '#F1F5F9', text: '#475569' },
  MEDIUM:   { bar: '#3B82F6', bg: '#EFF6FF', pill: '#DBEAFE', text: '#1D4ED8' },
  HIGH:     { bar: '#F97316', bg: '#FFF7ED', pill: '#FFEDD5', text: '#C2410C' },
  CRITICAL: { bar: '#EF4444', bg: '#FEF2F2', pill: '#FEE2E2', text: '#DC2626' },
};

const STATUS_META = {
  PENDING:     { dot: '#94A3B8', label: 'Pending',     bg: '#F1F5F9', text: '#475569' },
  IN_PROGRESS: { dot: '#3B82F6', label: 'In Progress', bg: '#DBEAFE', text: '#1D4ED8' },
  ON_HOLD:     { dot: '#F59E0B', label: 'On Hold',     bg: '#FEF3C7', text: '#B45309' },
  DONE:        { dot: '#10B981', label: 'Done',         bg: '#D1FAE5', text: '#065F46' },
  OVERDUE:     { dot: '#EF4444', label: 'Overdue',      bg: '#FEE2E2', text: '#DC2626' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0,0,0,0);
  const diff = Math.floor((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function TaskCard({ task }) {
  const navigate    = useNavigate();
  const isOverdue   = task.status === 'OVERDUE';
  const isDueToday  = task.deadline === new Date().toISOString().split('T')[0];
  const isDone      = task.status === 'DONE';
  const pc          = PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.MEDIUM;
  const sm          = STATUS_META[task.status]      || STATUS_META.PENDING;
  const overdueDays = task.daysOverdue > 0 ? task.daysOverdue : 0;

  return (
    <div
      onClick={() => navigate(`/tasks/${task.id}`)}
      style={{
        background: '#fff',
        borderRadius: '14px',
        border: `1px solid ${isOverdue ? '#FECACA' : '#E5E7EB'}`,
        padding: '0',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, transform 0.15s',
        opacity: isDone ? 0.75 : 1,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Priority bar at top */}
      <div style={{ height: '3px', background: pc.bar, width: '100%' }} />

      <div style={{ padding: '14px 16px' }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
          <p style={{
            fontWeight: 600, fontSize: '14px', color: '#111827',
            lineHeight: 1.4, letterSpacing: '-0.01em',
            flex: 1, minWidth: 0,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            textDecoration: isDone ? 'line-through' : 'none',
          }}>{task.title}</p>

          {/* Deadline chip */}
          <div style={{
            flexShrink: 0,
            padding: '4px 10px',
            borderRadius: '999px',
            background: isOverdue ? '#FEE2E2' : isDueToday ? '#FEF3C7' : '#F1F5F9',
            color:       isOverdue ? '#DC2626' : isDueToday ? '#B45309' : '#6B7280',
            fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            {isOverdue
              ? `${overdueDays}d late`
              : formatDate(task.deadline)
            }
          </div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {/* Status pill */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '3px 8px', borderRadius: '999px',
              background: sm.bg, color: sm.text,
              fontSize: '11px', fontWeight: 600,
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: sm.dot, flexShrink: 0 }} />
              {sm.label}
            </span>

            {/* Priority */}
            <span style={{
              padding: '3px 8px', borderRadius: '999px',
              background: pc.pill, color: pc.text,
              fontSize: '11px', fontWeight: 600,
            }}>{task.priority}</span>

            {/* Dept */}
            {task.department && (
              <span style={{
                padding: '3px 8px', borderRadius: '999px',
                background: '#F8FAFC', color: '#6B7280',
                fontSize: '11px', border: '1px solid #E5E7EB',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: '90px',
              }}>{task.department.name}</span>
            )}
          </div>

          {/* Assignee avatar */}
          {task.assignedTo && (
            <div title={task.assignedTo.name} style={{ flexShrink: 0 }}>
              <Avatar name={task.assignedTo.name} size={26} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
